/**
 * XSD parser — XML Schema (inline in a WSDL, an ISO 20022 message, or a bare `.xsd`) →
 * intermediate `{ name, fields:[{ name, xsdType, enumValues, required, isList }] }`.
 *
 * Mirrors the corpus converter's XSD walk (scripts/convert-tool-schemas.ts `_walkXmlForTypes`
 * / `xsdFacets` / `buildSimpleTypeMap`): one entity per `<xs:complexType name>`, one field per
 * `<xs:element>`; `<xs:simpleType>` restrictions resolve facets (enum values), `minOccurs=0`
 * → optional, `maxOccurs=unbounded` → list. Uses the same `fast-xml-parser` config the
 * converter uses (`removeNSPrefix` so `xs:`/`xsd:` prefixes collapse to uniform keys). The
 * converter writes manifest-meta sidecars; the SOAP + ISO 20022 readers build canonical
 * entities — the parse step is the shared half.
 */

import { XMLParser } from 'fast-xml-parser';

export interface XsdField {
  readonly name: string;
  /** Local XSD type name with any namespace prefix stripped (e.g. `Max35Text`, `string`). */
  readonly xsdType: string;
  readonly required: boolean;
  readonly isList: boolean;
  readonly enumValues?: string[] | undefined;
}

export interface XsdType {
  readonly name: string;
  readonly fields: XsdField[];
}

// Same configuration the converter uses — strip ns prefixes so element/type keys are uniform.
const xmlParser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  removeNSPrefix: true,
  allowBooleanAttributes: true,
});

const localName = (t: string): string => t.split(':').pop() ?? t;

/** Index every `<xs:simpleType name="X"><restriction>` so an element's @_type ref resolves. */
function buildSimpleTypeMap(node: unknown, map: Map<string, unknown>): void {
  if (!node || typeof node !== 'object') return;
  const rec = node as Record<string, unknown>;
  const sts = Array.isArray(rec['simpleType']) ? rec['simpleType'] : (rec['simpleType'] ? [rec['simpleType']] : []);
  for (const st of sts) {
    const s = st as Record<string, unknown>;
    if (typeof s['@_name'] === 'string' && s['restriction']) map.set(s['@_name'] as string, s['restriction']);
  }
  for (const v of Object.values(rec)) {
    if (Array.isArray(v)) { for (const c of v) buildSimpleTypeMap(c, map); }
    else if (v && typeof v === 'object') buildSimpleTypeMap(v, map);
  }
}

/** `<restriction>` → enum members (the `<enumeration value="…"/>` facets). */
function restrictionEnum(restriction: unknown): string[] | undefined {
  if (!restriction || typeof restriction !== 'object') return undefined;
  const en = (restriction as Record<string, unknown>)['enumeration'];
  if (!en) return undefined;
  const arr = Array.isArray(en) ? en : [en];
  const vals = arr
    .map((e) => (e && typeof e === 'object') ? (e as Record<string, unknown>)['@_value'] : undefined)
    .filter((v): v is string => typeof v === 'string');
  return vals.length ? vals : undefined;
}

function extractFields(sub: unknown, simpleTypeMap: Map<string, unknown>): XsdField[] {
  const fields: XsdField[] = [];
  const visit = (n: unknown): void => {
    if (!n || typeof n !== 'object') return;
    const r = n as Record<string, unknown>;
    const elements = Array.isArray(r['element']) ? r['element'] : (r['element'] ? [r['element']] : []);
    for (const el of elements) {
      const ee = el as Record<string, unknown>;
      const name = ee['@_name'] ?? ee['@_ref'];
      if (typeof name !== 'string') continue;
      const rawType = typeof ee['@_type'] === 'string' ? (ee['@_type'] as string) : 'unknown';
      const xsdType = localName(rawType);
      const minOccurs = ee['@_minOccurs'];
      const maxOccurs = ee['@_maxOccurs'];
      const required = minOccurs === undefined ? true : String(minOccurs) !== '0';
      const isList = String(maxOccurs ?? '') === 'unbounded' || (typeof maxOccurs === 'number' && maxOccurs > 1);
      const inlineRestriction = (ee['simpleType'] as Record<string, unknown> | undefined)?.['restriction'];
      const restriction = inlineRestriction ?? simpleTypeMap.get(xsdType);
      fields.push({ name, xsdType, required, isList, enumValues: restrictionEnum(restriction) });
    }
    // Recurse the sequence/choice/all compositors.
    for (const key of ['sequence', 'choice', 'all']) if (r[key]) visit(r[key]);
  };
  visit(sub);
  return fields;
}

function walk(node: unknown, types: XsdType[], simpleTypeMap: Map<string, unknown>): void {
  if (!node || typeof node !== 'object') return;
  const rec = node as Record<string, unknown>;

  const complexTypes = Array.isArray(rec['complexType']) ? rec['complexType'] : (rec['complexType'] ? [rec['complexType']] : []);
  for (const ct of complexTypes) {
    const cc = ct as Record<string, unknown>;
    const name = cc['@_name'];
    if (typeof name !== 'string') continue;
    const fields = extractFields(cc, simpleTypeMap);
    if (fields.length) types.push({ name, fields });
  }

  // Recurse into common nesting containers (schema, types, definitions, …).
  for (const v of Object.values(rec)) {
    if (Array.isArray(v)) { for (const c of v) walk(c, types, simpleTypeMap); }
    else if (v && typeof v === 'object') walk(v, types, simpleTypeMap);
  }
}

export function parseXsd(xml: string): XsdType[] {
  let doc: unknown;
  try { doc = xmlParser.parse(xml); } catch { return []; }
  const simpleTypeMap = new Map<string, unknown>();
  buildSimpleTypeMap(doc, simpleTypeMap);
  const types: XsdType[] = [];
  walk(doc, types, simpleTypeMap);
  // Dedupe by name (a type may surface through several container recursions).
  const seen = new Set<string>();
  return types.filter((t) => (seen.has(t.name) ? false : (seen.add(t.name), true)));
}
